import { useId, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { ErrorBoundary } from '../lib/ErrorBoundary'
import type { NavGroup } from '../lib/nav'
import { CHANGE_PASSWORD_PATH, canAccess, isDelegating, isGroup, isWide, navFor } from '../lib/nav'
import { useAuthedSession, useSession } from '../lib/SessionContext'
import { sessionLabel } from '../lib/session'
import { DelegateBanner } from './admins/DelegateBanner'

/** layout ของทุกหน้าหลังล็อกอิน — header + แถบเมนู + เนื้อหา */
export default function Shell() {
  const { signOut } = useSession()
  const session = useAuthedSession()
  const { pathname } = useLocation()
  const [leaving, setLeaving] = useState(false)

  // แอดมินที่ยังใช้รหัสตั้งต้น: App.tsx บังคับพามาหน้าเปลี่ยนรหัสอยู่แล้ว
  // ที่นี่แค่ซ่อนเมนูด้วย ไม่งั้นเมนูโชว์ให้กดแต่กดแล้วเด้งกลับ = ดูเหมือนพัง
  const mustChangePassword = session.kind === 'admin' && session.mustChangePassword
  const items = mustChangePassword ? [] : navFor(session)

  async function onSignOut() {
    setLeaving(true)
    await signOut()
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-brand">
          {/* โลโก้ทางการจาก simat.co.th — ต้องอยู่บนพื้นขาวเสมอ (.brand-logo จัดให้)
              เพราะตัวอักษรในไฟล์เป็นสีดำ/เทา วางบนแถบน้ำเงินหรือธีมมืดแล้วอ่านไม่ออก */}
          <img className="brand-logo" src="/simat-logo.png" alt="SIMAT Technologies" />
          <span className="chip">{session.company}</span>
        </div>

        <div className="topbar-who">
          <span className="who-name">{session.displayName}</span>
          <span className="who-kind">{sessionLabel(session)}</span>
          {session.kind === 'admin' && (
            <Link className="ghost" to={CHANGE_PASSWORD_PATH}>
              เปลี่ยนรหัสผ่าน
            </Link>
          )}
          <button type="button" className="ghost" onClick={onSignOut} disabled={leaving}>
            {leaving ? 'กำลังออก…' : 'ออกจากระบบ'}
          </button>
        </div>
      </header>

      {/* อยู่นอก shell-body เพื่อกินความกว้างเต็มจอ — ต้องเห็นชัดว่ากำลังดูแทนใครอยู่
          ไม่งั้น admin จะลืมตัวแล้วเข้าใจผิดว่าข้อมูลที่เห็นเป็นของตัวเอง */}
      {isDelegating(session) && <DelegateBanner session={session} />}

      <div className={items.length > 0 ? 'shell-body' : 'shell-body shell-body--nonav'}>
        {items.length > 0 && (
          <nav className="sidenav">
            {items.map((item) =>
              isGroup(item) ? (
                <NavGroupBlock key={item.label} group={item} pathname={pathname} />
              ) : (
                // `end` เฉพาะ "/" ไม่งั้นหน้าแรกจะขึ้น active ตลอดทุกหน้า
                // sidenav__user = เมนูฝั่งพนักงาน แยกสีจากเครื่องมือแอดมิน (ดู nav.ts: section)
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  // className เป็นฟังก์ชันเพื่อคง active ที่ NavLink ใส่ให้เอง แล้วเติม sidenav__user
                  className={({ isActive }) =>
                    [isActive && 'active', item.section === 'user' && 'sidenav__user']
                      .filter(Boolean)
                      .join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>
        )}

        {/* ความกว้างมาจากตารางใน nav.ts ที่เดียว ไม่ใช่หน้าลูกบอกขึ้นมา —
            หน้าใหม่ที่ลืมส่งค่าจะได้ค่าเริ่มต้น (จำกัดความกว้าง) ซึ่งปลอดภัยกว่าเดาผิดทาง */}
        <main className={isWide(pathname) ? 'wide' : undefined}>
          {canAccess(session, pathname) ? (
            /* key = pathname → ย้ายหน้าแล้วรีเซ็ต boundary ให้เอง
               ไม่งั้นหน้าหนึ่งพังแล้วกดเมนูไปหน้าอื่นจะยังเห็นกล่อง error ค้างอยู่ */
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          ) : (
            <section className="card">
              <h1>เข้าหน้านี้ไม่ได้</h1>
              <p>
                บัญชีที่ใช้อยู่ ({sessionLabel(session)}) ไม่มีสิทธิ์เข้าหน้า <code>{pathname}</code>
              </p>
              <p>
                <Link to="/">กลับหน้าแรก</Link>
              </p>
            </section>
          )}
        </main>
      </div>
    </div>
  )
}

/**
 * หัวข้อที่พับได้ + เมนูย่อยข้างใน
 *
 * ── ทำไมไม่เก็บ "กางอยู่ไหม" เป็น state ตรง ๆ ────────────────────────────────
 * ถ้าเก็บตรง ๆ จะต้องมี effect คอยกางให้เองตอนย้ายหน้า แล้ว effect ตัวนั้นจะไป
 * กางซ้ำทับสิ่งที่คนเพิ่งกดพับ ที่นี่จึงคิดค่าตั้งต้นจาก url (หน้าปัจจุบันอยู่ใน
 * หมวดนี้ = กางไว้) แล้วให้ "ที่คนกดเอง" ทับค่านั้นได้ ไม่ต้องมี effect เลย
 *
 * พอย้ายไปหน้านอกหมวด ค่าที่คนกดยังอยู่ — ตั้งใจ เพราะเขาสั่งพับหมวดนี้ไว้เอง
 */
function NavGroupBlock({ group, pathname }: { group: NavGroup; pathname: string }) {
  const [chosen, setChosen] = useState<boolean | null>(null)
  const panelId = useId()

  // เทียบตรงตัวได้เพราะเมนูย่อยยังไม่มี route ที่มี parameter (กติกาเดียวกับ canAccess)
  const holdsCurrentPage = group.children.some((child) => child.path === pathname)
  const open = chosen ?? holdsCurrentPage

  return (
    <div className="navgroup">
      <button
        type="button"
        className="navgroup__head"
        onClick={() => setChosen(!open)}
        /* aria-expanded/-controls บอก screen reader ว่านี่คือปุ่มพับ ไม่ใช่ลิงก์ที่ตายแล้ว */
        aria-expanded={open}
        aria-controls={panelId}
        title={group.note}
      >
        <span className="navgroup__label">{group.label}</span>
        {/* จุด: พับอยู่แต่หน้าที่เปิดค้างอยู่ข้างใน — ไม่งั้นแถบเมนูจะไม่มีอะไร active
            เลยสักอัน แล้วคนอ่านว่า "หลงมาอยู่ตรงไหนไม่รู้" */}
        {!open && holdsCurrentPage && <span className="navgroup__dot" aria-hidden="true" />}
        <svg
          className={open ? 'navgroup__caret navgroup__caret--open' : 'navgroup__caret'}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M7 4.5 12.5 10 7 15.5" />
        </svg>
      </button>

      {open && (
        <div className="navgroup__items" id={panelId}>
          {group.children.map((child) => (
            <NavLink key={child.path} to={child.path}>
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}
